/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.model

import kotlinx.serialization.Serializable

/**
 * An Insight is an ordered list of parts.  This list does have a few representation invariants:
 */
@Serializable
data class Insight(
    val parts: List<Part>
) {
    companion object

    @Serializable
    sealed class Part {
        @Serializable
        data class Header(val text: String) : Part()

        @Serializable
        data class SubHeader(val text: String) : Part() {
            fun print() = text
        }

        @Serializable
        data class Image(val altText: String, val url: String) :
            Part() // would be nice to have a stronger type, but no cross-platform URL object exists.  Maybe we can type-alias something with expect/actual for different platforms?

        @Serializable
        data class Paragraph(val sentences: List<Sentence>) : Part() {
            fun print() = sentences.joinToString("  ") { it.print() }
        }
    }

    @Serializable
    data class Sentence(val string: String) {
        fun print() = string
    }
}

fun List<Insight.Part.Paragraph>.print() = joinToString("\n\n") { it.print() }

fun List<Insight.Part>.filterEmpty(): List<Insight.Part> =
    filter {
        if (it is Insight.Part.Paragraph) {
            it.sentences.isNotEmpty()
        } else {
            true
        }
    }

operator fun Insight.Part.plus(collection: Collection<Insight.Part>): List<Insight.Part> {
    return listOf(this) + collection
}

fun Insight.Companion.new(
    overview: List<Insight.Part>,
    stress: List<Insight.Part>,
    attention: List<Insight.Part>,
    motivation: List<Insight.Part>,
    summary: List<Insight.Part>
) = Insight(
    buildList<Insight.Part> {
        add(Insight.Part.Header("Overview"))
        addAll(overview)

        add(Insight.Part.Header("Stress"))
        addAll(stress)

        add(Insight.Part.Header("Attention"))
        addAll(attention)

        add(Insight.Part.Header("Motivation"))
        addAll(motivation)

        add(Insight.Part.Header("Summary"))
        addAll(summary)
    }
)
